import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    const priceContainer = this.querySelector('[ref="priceContainer"]');

    const currentMsrp = this.querySelector('.msrp');
    const newMsrp = newProductPrice.querySelector('.msrp');
    if (!newMsrp) {
      currentMsrp?.remove();
    } else if (!currentMsrp) {
      priceContainer?.insertAdjacentElement('afterend', /** @type {Element} */ (newMsrp.cloneNode(true)));
    } else {
      currentMsrp.replaceWith(newMsrp);
    }

    const currentPromo = this.querySelector('.price-promo-messaging');
    const newPromo = newProductPrice.querySelector('.price-promo-messaging');
    const promoInsertAnchor = this.querySelector('.msrp') ?? priceContainer;
    if (!newPromo) {
      currentPromo?.remove();
    } else if (!currentPromo) {
      promoInsertAnchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newPromo.cloneNode(true)));
    } else {
      currentPromo.replaceWith(newPromo);
    }

    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');
    const noteInsertAnchor = this.querySelector('.price-promo-messaging') ?? this.querySelector('.msrp') ?? priceContainer;
    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      noteInsertAnchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
